/**
 * FieldLogsTab
 * ------------
 * Daily field logs: manpower headcount and man-hours, auto-pulled weather,
 * delays, safety, inspections and site photos. Contractors file them, the PM
 * approves, and an approved log locks as the contemporaneous record.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, Download, Send, Trash2, Upload, ClipboardList, CloudSun,
  AlertTriangle, ShieldAlert, Camera, Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API } from "@/App";

const DELAY_TYPES = [
  { value: "weather", label: "Weather" },
  { value: "material", label: "Material / delivery" },
  { value: "manpower", label: "Manpower shortage" },
  { value: "design", label: "RFI / design clarification" },
  { value: "owner", label: "Owner directive" },
  { value: "inspection", label: "Inspection / AHJ hold" },
  { value: "access", label: "Site access / infection control" },
  { value: "utility", label: "Utility shutdown" },
  { value: "other", label: "Other" },
];

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-600 ring-slate-300",
  submitted: "bg-amber-50 text-amber-700 ring-amber-300",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  revise_requested: "bg-red-50 text-red-700 ring-red-300",
};

const today = () => new Date().toISOString().slice(0, 10);

const blank = () => ({
  log_date: today(), responsible_company: "", trade: "", superintendent: "",
  work_performed: "", areas_worked: "", visitors: "", toolbox_talk: "",
  safety_incident: false, safety_notes: "", ppe_compliant: true,
  icra_permit_number: "", notes: "",
  crews: [{ company: "", trade: "", workers: 1, hours: 8, notes: "" }],
  delays: [], equipment: [], inspections: [],
  weather: { conditions: "", temp_high: null, temp_low: null, precipitation_in: null, wind_mph: null, source: "manual" },
});

const Stat = ({ label, value, accent = "text-slate-900", sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
    <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className={`text-lg font-black mt-1 ${accent}`}>{value}</p>
    {sub ? <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p> : null}
  </div>
);

const Label = ({ children }) => (
  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">{children}</label>
);

const weatherLine = (w) => {
  if (!w) return "—";
  const bits = [w.conditions];
  if (w.temp_high != null) bits.push(`${Math.round(w.temp_high)}°/${w.temp_low != null ? Math.round(w.temp_low) : "—"}°F`);
  if (w.precipitation_in) bits.push(`${w.precipitation_in}" rain`);
  if (w.wind_mph) bits.push(`${Math.round(w.wind_mph)} mph`);
  return bits.filter(Boolean).join(" · ") || "—";
};

export default function FieldLogsTab({ projectId, project, token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [review, setReview] = useState({ status: "approved", pm_notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/field-logs?project_id=${projectId}`, { headers });
      setRows(r.data?.field_logs || []);
      setSummary(r.data?.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the field logs");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);
  useEffect(() => { load(); }, [load]);

  const pullWeather = async () => {
    setPulling(true);
    try {
      const r = await axios.get(
        `${API}/field-logs/weather?project_id=${projectId}&log_date=${form.log_date}`, { headers });
      setForm((f) => ({ ...f, weather: r.data.weather }));
      toast.success(`Weather pulled for ${r.data.location}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Weather lookup failed");
    } finally { setPulling(false); }
  };

  const save = async () => {
    if (!form.work_performed.trim()) return toast.error("Describe the work performed");
    setSaving(true);
    try {
      const body = {
        ...form,
        crews: (form.crews || []).filter((c) => c.company || c.workers),
        delays: (form.delays || []).filter((d) => d.description),
        equipment: (form.equipment || []).filter((e) => e.name),
        inspections: (form.inspections || []).filter((i) => i.agency || i.inspection_type),
      };
      if (editingId) await axios.put(`${API}/field-logs/${editingId}`, body, { headers });
      else await axios.post(`${API}/field-logs`, { project_id: projectId, ...body }, { headers });
      toast.success(`Daily log for ${form.log_date} saved`);
      setForm(null); setEditingId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the log");
    } finally { setSaving(false); }
  };

  const act = async (row, path, label, body = {}) => {
    try {
      await axios.post(`${API}/field-logs/${row.id}/${path}`, body, { headers });
      toast.success(`${row.log_date} ${label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const doReview = async () => {
    await act(reviewing, "review", `marked ${review.status.replace(/_/g, " ")}`, review);
    setReviewing(null);
  };

  const uploadPhoto = async (row, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/field-logs/${row.id}/photos`, fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} added to ${row.log_date}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete the log for ${row.log_date}?`)) return;
    try {
      await axios.delete(`${API}/field-logs/${row.id}`, { headers });
      toast.success("Log deleted"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const openPdf = async (row) => {
    try {
      const r = await axios.get(`${API}/field-logs/${row.id}/pdf`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `daily-log-${row.log_date}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the daily report");
    }
  };

  const setRow = (key, i, field, value) => setForm((f) => ({
    ...f, [key]: f[key].map((r, j) => (j === i ? { ...r, [field]: value } : r)),
  }));
  const addRow = (key, row) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), row] }));
  const dropRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));

  if (loading) {
    return <p className="text-slate-500 inline-flex items-center gap-2 py-8">
      <Loader2 className="w-4 h-4 animate-spin" />Loading field logs…</p>;
  }

  return (
    <div className="space-y-5" data-testid="field-logs-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Daily Field Logs</h2>
          <p className="text-sm text-slate-500">
            Headcount, man-hours, weather, delays and photos — the contemporaneous record that backs a delay claim.
          </p>
        </div>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => {
            setEditingId(null);
            setForm({
              ...blank(),
              superintendent: project?.pm_name || "",
              crews: [{ company: "", trade: "", workers: 1, hours: 8, notes: "" }],
            });
          }}
          data-testid="new-field-log-btn">
          <Plus className="w-4 h-4 mr-2" />New Daily Log
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="field-log-summary">
          <Stat label="Logs filed" value={summary.total_logs}
            sub={`${summary.approved} approved`} />
          <Stat label="Awaiting approval" value={summary.pending_approval}
            accent={summary.pending_approval ? "text-amber-600" : "text-slate-900"} />
          <Stat label="Man-hours" value={summary.total_manhours.toLocaleString()}
            sub={`Peak ${summary.peak_headcount} on site`} />
          <Stat label="Hours lost" value={summary.hours_lost}
            accent={summary.hours_lost ? "text-red-600" : "text-slate-900"}
            sub={`${summary.delay_days} delay day(s)`} />
          <Stat label="Safety incidents" value={summary.safety_incidents}
            accent={summary.safety_incidents ? "text-red-600" : "text-emerald-600"}
            sub={`${summary.photos} photo(s)`} />
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-center py-12 rounded-xl border border-dashed border-slate-300 bg-white"
          data-testid="field-logs-empty">
          <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold">No daily logs filed yet</p>
          <p className="text-sm text-slate-400 mt-1">
            One log per trade per day. Weather pulls automatically from the project city.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-sm">
          <table className="w-full min-w-[1080px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Date", "Company / trade", "Crew", "Weather", "Delays", "Photos", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/70" data-testid={`field-log-row-${row.log_date}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-bold text-blue-700">{row.log_date}</span>
                    {row.safety_incident ? (
                      <span className="block text-[10px] font-bold text-red-600 inline-flex items-center gap-1 mt-0.5">
                        <ShieldAlert className="w-3 h-3" />Incident
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{row.responsible_company || "—"}</p>
                    <p className="text-xs text-slate-500">{row.trade || row.superintendent || "—"}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">
                    <span className="font-bold">{row.total_workers}</span> on site
                    <span className="block text-[11px] text-slate-500">{row.total_manhours} man-hours</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[190px]">{weatherLine(row.weather)}</td>
                  <td className="px-4 py-3 text-sm">
                    {row.delay_count ? (
                      <span className="text-red-600 font-bold inline-flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" />{row.total_hours_lost} hr
                      </span>
                    ) : <span className="text-slate-400">None</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{row.photo_count || 0}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${STATUS_STYLE[row.status]}`}>
                      {(row.status || "").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {["draft", "revise_requested"].includes(row.status) && (
                        <Button size="sm" variant="ghost" className="text-amber-700 hover:bg-amber-50"
                          onClick={() => act(row, "submit", "submitted for approval")}
                          data-testid={`field-log-submit-${row.log_date}`}>
                          <Send className="w-3.5 h-3.5 mr-1" />Submit
                        </Button>
                      )}
                      {row.status === "submitted" && (
                        <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50"
                          onClick={() => { setReviewing(row); setReview({ status: "approved", pm_notes: "" }); }}
                          data-testid={`field-log-review-${row.log_date}`}>
                          Review
                        </Button>
                      )}
                      {row.status === "approved" && (
                        <Button size="sm" variant="ghost" className="text-slate-500 hover:bg-slate-100"
                          onClick={() => act(row, "reopen", "reopened")} title="Reopen for edit"
                          data-testid={`field-log-reopen-${row.log_date}`}>
                          <Unlock className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {!row.is_locked && (
                        <label className="inline-flex items-center px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100 cursor-pointer"
                          title="Add a site photo" data-testid={`field-log-photo-${row.log_date}`}>
                          <Camera className="w-3.5 h-3.5" />
                          <input type="file" accept="image/*,application/pdf" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(row, f); e.target.value = ""; }} />
                        </label>
                      )}
                      {(row.photos || []).length > 0 && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${row.photos[row.photos.length - 1].url}`}
                          target="_blank" rel="noreferrer" title="Open latest photo"
                          className="px-2 py-1 text-slate-500 hover:text-slate-800">
                          <Upload className="w-3.5 h-3.5 rotate-180" />
                        </a>
                      )}
                      {!row.is_locked && (
                        <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                          onClick={() => { setEditingId(row.id); setForm({ ...blank(), ...row, weather: row.weather || blank().weather }); }}>
                          Edit
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-blue-700 hover:bg-blue-50"
                        onClick={() => openPdf(row)} data-testid={`field-log-pdf-${row.log_date}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {!row.is_locked && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                          onClick={() => remove(row)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit daily log" : "File a daily log"}</DialogTitle>
            <DialogDescription>
              Who was on site, what got built, what held you up. Weather pulls from the project city.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-4 gap-3">
                <div>
                  <Label>Log date</Label>
                  <Input type="date" value={form.log_date}
                    onChange={(e) => setForm({ ...form, log_date: e.target.value })}
                    data-testid="field-log-date-input" />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input value={form.responsible_company} placeholder="Trade partner"
                    onChange={(e) => setForm({ ...form, responsible_company: e.target.value })}
                    data-testid="field-log-company-input" />
                </div>
                <div>
                  <Label>Trade</Label>
                  <Input value={form.trade} placeholder="electrical"
                    onChange={(e) => setForm({ ...form, trade: e.target.value })} />
                </div>
                <div>
                  <Label>Superintendent</Label>
                  <Input value={form.superintendent}
                    onChange={(e) => setForm({ ...form, superintendent: e.target.value })} />
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Weather</p>
                  <Button size="sm" variant="outline" onClick={pullWeather} disabled={pulling}
                    data-testid="field-log-pull-weather-btn">
                    {pulling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      : <CloudSun className="w-3.5 h-3.5 mr-1" />}Auto-pull
                  </Button>
                </div>
                <div className="grid sm:grid-cols-5 gap-2">
                  <Input placeholder="Conditions" value={form.weather?.conditions || ""}
                    onChange={(e) => setForm({ ...form, weather: { ...form.weather, conditions: e.target.value, source: "manual" } })}
                    data-testid="field-log-weather-conditions-input" />
                  <Input type="number" placeholder="High °F" value={form.weather?.temp_high ?? ""}
                    onChange={(e) => setForm({ ...form, weather: { ...form.weather, temp_high: e.target.value === "" ? null : Number(e.target.value) } })} />
                  <Input type="number" placeholder="Low °F" value={form.weather?.temp_low ?? ""}
                    onChange={(e) => setForm({ ...form, weather: { ...form.weather, temp_low: e.target.value === "" ? null : Number(e.target.value) } })} />
                  <Input type="number" step="0.01" placeholder='Rain "' value={form.weather?.precipitation_in ?? ""}
                    onChange={(e) => setForm({ ...form, weather: { ...form.weather, precipitation_in: e.target.value === "" ? null : Number(e.target.value) } })} />
                  <Input type="number" placeholder="Wind mph" value={form.weather?.wind_mph ?? ""}
                    onChange={(e) => setForm({ ...form, weather: { ...form.weather, wind_mph: e.target.value === "" ? null : Number(e.target.value) } })} />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Source: {form.weather?.source || "manual"}</p>
              </div>

              <div>
                <Label>Work performed</Label>
                <Textarea rows={3} value={form.work_performed}
                  placeholder="Set isolation room door frames 2-114 through 2-118; rough-in med gas above corridor."
                  onChange={(e) => setForm({ ...form, work_performed: e.target.value })}
                  data-testid="field-log-work-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Areas worked</Label>
                  <Input value={form.areas_worked} placeholder="Level 2 nursery, corridor 2A"
                    onChange={(e) => setForm({ ...form, areas_worked: e.target.value })} />
                </div>
                <div>
                  <Label>ICRA permit no.</Label>
                  <Input value={form.icra_permit_number} placeholder="ICRA-001"
                    onChange={(e) => setForm({ ...form, icra_permit_number: e.target.value })} />
                </div>
              </div>

              {/* Manpower */}
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">Manpower</p>
                {(form.crews || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2 items-center">
                    <Input className="sm:col-span-4" placeholder="Company" value={c.company}
                      onChange={(e) => setRow("crews", i, "company", e.target.value)}
                      data-testid={`field-log-crew-company-${i}`} />
                    <Input className="sm:col-span-3" placeholder="Trade" value={c.trade}
                      onChange={(e) => setRow("crews", i, "trade", e.target.value)} />
                    <Input className="sm:col-span-2" type="number" placeholder="Workers" value={c.workers}
                      onChange={(e) => setRow("crews", i, "workers", Number(e.target.value))}
                      data-testid={`field-log-crew-workers-${i}`} />
                    <Input className="sm:col-span-2" type="number" step="0.5" placeholder="Hours" value={c.hours}
                      onChange={(e) => setRow("crews", i, "hours", Number(e.target.value))} />
                    <Button size="icon" variant="ghost" className="sm:col-span-1"
                      onClick={() => dropRow("crews", i)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <Button size="sm" variant="outline"
                    onClick={() => addRow("crews", { company: "", trade: "", workers: 1, hours: 8, notes: "" })}
                    data-testid="field-log-add-crew-btn">
                    <Plus className="w-3 h-3 mr-1" />Add crew
                  </Button>
                  <p className="text-xs font-bold text-slate-700">
                    {(form.crews || []).reduce((s, c) => s + Number(c.workers || 0), 0)} workers ·{" "}
                    {(form.crews || []).reduce((s, c) => s + Number(c.workers || 0) * Number(c.hours || 0), 0)} man-hours
                  </p>
                </div>
              </div>

              {/* Delays */}
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">Delays &amp; disruptions</p>
                {(form.delays || []).map((d, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2 items-center">
                    <div className="sm:col-span-3">
                      <Select value={d.delay_type} onValueChange={(v) => setRow("delays", i, "delay_type", v)}>
                        <SelectTrigger data-testid={`field-log-delay-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DELAY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input className="sm:col-span-6" placeholder="What happened" value={d.description}
                      onChange={(e) => setRow("delays", i, "description", e.target.value)}
                      data-testid={`field-log-delay-desc-${i}`} />
                    <Input className="sm:col-span-2" type="number" step="0.5" placeholder="Hrs lost"
                      value={d.hours_lost}
                      onChange={(e) => setRow("delays", i, "hours_lost", Number(e.target.value))} />
                    <Button size="icon" variant="ghost" className="sm:col-span-1"
                      onClick={() => dropRow("delays", i)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline"
                  onClick={() => addRow("delays", { delay_type: "weather", description: "", hours_lost: 0 })}
                  data-testid="field-log-add-delay-btn">
                  <Plus className="w-3 h-3 mr-1" />Add delay
                </Button>
              </div>

              {/* Safety */}
              <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Safety</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Toolbox talk</Label>
                    <Input value={form.toolbox_talk} placeholder="Silica dust control"
                      onChange={(e) => setForm({ ...form, toolbox_talk: e.target.value })} />
                  </div>
                  <div>
                    <Label>Visitors / inspections on site</Label>
                    <Input value={form.visitors} placeholder="Infection Prevention walked the barrier"
                      onChange={(e) => setForm({ ...form, visitors: e.target.value })} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 pt-1">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={!!form.safety_incident}
                      onChange={(e) => setForm({ ...form, safety_incident: e.target.checked })}
                      data-testid="field-log-incident-checkbox" />
                    Safety incident today
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={!!form.ppe_compliant}
                      onChange={(e) => setForm({ ...form, ppe_compliant: e.target.checked })} />
                    PPE compliant
                  </label>
                </div>
                {form.safety_incident && (
                  <Textarea rows={2} value={form.safety_notes} placeholder="What happened, who was involved, action taken"
                    onChange={(e) => setForm({ ...form, safety_notes: e.target.value })}
                    data-testid="field-log-safety-notes-input" />
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} disabled={saving}
              data-testid="field-log-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review the log for {reviewing?.log_date}</DialogTitle>
            <DialogDescription>
              {reviewing?.responsible_company} · {reviewing?.total_workers} workers ·{" "}
              {reviewing?.total_manhours} man-hours · {reviewing?.total_hours_lost} hr lost.
              Approving locks the log as the project record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Decision</Label>
              <Select value={review.status} onValueChange={(v) => setReview({ ...review, status: v })}>
                <SelectTrigger data-testid="field-log-decision-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approve</SelectItem>
                  <SelectItem value="revise_requested">Send back for revision</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes to the trade</Label>
              <Textarea rows={3} value={review.pm_notes}
                placeholder="Break out the barrier hold separately so it lands on the delay log."
                onChange={(e) => setReview({ ...review, pm_notes: e.target.value })}
                data-testid="field-log-pm-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewing(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doReview}
              data-testid="field-log-review-submit-btn">Record decision</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
